import { useEffect, useRef, useState } from 'react';
import { MoreVertical, RefreshCw, MapPin, Briefcase, GraduationCap, Languages, Award } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Header, DetailSkeleton, PullToRefresh } from '@ah/design/mobile';
import { gql } from '../api';
import { useMe } from '../useAuth';

interface Experience {
  id: string;
  title: string | null;
  company: string | null;
  location: string | null;
  employmentType: string | null;
  duration: string | null;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean | null;
  organizationPictureUrl: string | null;
}

interface Education {
  id: string;
  school: string | null;
  degree: string | null;
  degreeName: string | null;
  fieldOfStudy: string | null;
  duration: string | null;
  startDate: string | null;
  endDate: string | null;
  organizationPictureUrl: string | null;
}

interface Language {
  id: string;
  language: string;
  proficiency: string | null;
}

interface Certification {
  id: string;
  name: string;
  issuer: string | null;
  issuedDate: string | null;
}

interface MyLinkedInProfile {
  experiences: Experience[];
  education: Education[];
  languages: Language[];
  certifications: Certification[];
}

const PROFILE_QUERY = /* GraphQL */ `
  query MyLinkedInProfile {
    myLinkedInProfile {
      experiences {
        id
        title
        company
        location
        employmentType
        duration
        startDate
        endDate
        isCurrent
        organizationPictureUrl
      }
      education {
        id
        school
        degree
        degreeName
        fieldOfStudy
        duration
        startDate
        endDate
        organizationPictureUrl
      }
      languages {
        id
        language
        proficiency
      }
      certifications {
        id
        name
        issuer
        issuedDate
      }
    }
  }
`;

function formatYear(iso: string | null): string | null {
  if (!iso) return null;
  const y = new Date(iso).getFullYear();
  return Number.isFinite(y) ? String(y) : null;
}

function formatRange(start: string | null, end: string | null, isCurrent?: boolean | null): string | null {
  const s = formatYear(start);
  const e = isCurrent ? 'Present' : formatYear(end);
  if (s && e) return `${s} – ${e}`;
  return s ?? e ?? null;
}

const RESYNC_MUTATION = /* GraphQL */ `
  mutation PeopleResyncSelf { peopleResyncSelf }
`;

export function Profile() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [syncToast, setSyncToast] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const profileQ = useQuery({
    queryKey: ['myLinkedInProfile'],
    queryFn: async () => {
      const data = await gql.request<{ myLinkedInProfile: MyLinkedInProfile }>(PROFILE_QUERY);
      return data.myLinkedInProfile;
    },
    enabled: !!me,
  });

  const resync = useMutation({
    mutationFn: async () => {
      await gql.request(RESYNC_MUTATION);
    },
    onSuccess: () => {
      setSyncToast('Sync started — your profile will update in a minute or two.');
      setTimeout(() => setSyncToast(null), 4000);
    },
    onError: () => {
      setSyncToast('Sync failed — please try again.');
      setTimeout(() => setSyncToast(null), 4000);
    },
  });

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: Event) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('touchstart', onClick);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('touchstart', onClick);
    };
  }, [menuOpen]);

  const kebabMenu = (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        aria-label="Profile menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        className="flex items-center justify-center w-9 h-9 rounded-lg text-foreground active:bg-accent focus:outline-none"
      >
        <MoreVertical size={20} />
      </button>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full mt-1 w-56 rounded-xl border border-border bg-popover shadow-lg overflow-hidden z-50"
          >
            <button
              type="button"
              role="menuitem"
              disabled={resync.isPending}
              onClick={() => {
                setMenuOpen(false);
                resync.mutate();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-left hover:bg-accent disabled:opacity-60"
            >
              <RefreshCw size={16} className={resync.isPending ? 'animate-spin' : ''} />
              {resync.isPending ? 'Syncing…' : 'Sync profile'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const toast = (
    <AnimatePresence>
      {syncToast && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 px-4 py-2 rounded-full bg-foreground text-background text-sm shadow-lg"
        >
          {syncToast}
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!me) {
    return (
      <div className="fixed inset-0 flex flex-col bg-background">
        <Header title="Profile" trailing={kebabMenu} />
        <main className="flex-1 overflow-y-auto">
          <DetailSkeleton hero="avatar" />
        </main>
        {toast}
      </div>
    );
  }

  const profile = profileQ.data;

  const handleRefresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['me'] }),
      qc.invalidateQueries({ queryKey: ['myLinkedInProfile'] }),
    ]);
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Profile" trailing={kebabMenu} />
      <PullToRefresh className="flex-1" onRefresh={handleRefresh}>
        {/* Hero */}
        <section className="relative">
          <div
            className="h-32 w-full bg-muted"
            style={
              me.backgroundPictureUrl
                ? { backgroundImage: `url(${me.backgroundPictureUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : undefined
            }
          />
          <div className="px-4 -mt-12 pb-4">
            <img
              src={me.pictureUrl ?? ''}
              alt=""
              className="w-24 h-24 rounded-full object-cover bg-muted border-4 border-background"
            />
            <div className="mt-3 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-semibold leading-tight">{me.name}</h1>
                {me.headline && (
                  <p className="text-sm text-muted-foreground mt-0.5">{me.headline}</p>
                )}
                {me.locationRaw && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin size={12} /> {me.locationRaw}
                  </p>
                )}
                {me.openToWork && (
                  <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                    Open to work
                  </span>
                )}
              </div>
            </div>

            {(me.followerCount != null || me.connectionCount != null) && (
              <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
                {me.connectionCount != null && (
                  <span><span className="font-semibold text-foreground">{me.connectionCount.toLocaleString()}</span> connections</span>
                )}
                {me.followerCount != null && (
                  <span><span className="font-semibold text-foreground">{me.followerCount.toLocaleString()}</span> followers</span>
                )}
              </div>
            )}
          </div>
        </section>

        <div className="px-4 pb-8 space-y-6">
          {me.about && (
            <Section title="About">
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{me.about}</p>
            </Section>
          )}

          {profile && profile.experiences.length > 0 && (
            <Section title="Experience" icon={<Briefcase size={14} />}>
              <div className="space-y-4">
                {profile.experiences.map((e) => {
                  const range = e.duration ?? formatRange(e.startDate, e.endDate, e.isCurrent);
                  return (
                    <div key={e.id} className="flex gap-3">
                      <OrgLogo url={e.organizationPictureUrl} fallback={<Briefcase size={18} />} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{e.title ?? '—'}</div>
                        {e.company && (
                          <div className="text-sm text-muted-foreground">
                            {e.company}
                            {e.employmentType ? ` · ${e.employmentType}` : ''}
                          </div>
                        )}
                        {range && <div className="text-xs text-muted-foreground mt-0.5">{range}</div>}
                        {e.location && (
                          <div className="text-xs text-muted-foreground">{e.location}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {profile && profile.education.length > 0 && (
            <Section title="Education" icon={<GraduationCap size={14} />}>
              <div className="space-y-4">
                {profile.education.map((e) => {
                  const range = e.duration ?? formatRange(e.startDate, e.endDate);
                  const detail = [e.degreeName ?? e.degree, e.fieldOfStudy].filter(Boolean).join(', ');
                  return (
                    <div key={e.id} className="flex gap-3">
                      <OrgLogo url={e.organizationPictureUrl} fallback={<GraduationCap size={18} />} />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{e.school ?? '—'}</div>
                        {detail && <div className="text-sm text-muted-foreground">{detail}</div>}
                        {range && <div className="text-xs text-muted-foreground mt-0.5">{range}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {profile && profile.languages.length > 0 && (
            <Section title="Languages" icon={<Languages size={14} />}>
              <div className="space-y-2">
                {profile.languages.map((l) => (
                  <div key={l.id} className="text-sm">
                    <span className="font-medium">{l.language}</span>
                    {l.proficiency && (
                      <span className="text-muted-foreground"> · {l.proficiency}</span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {profile && profile.certifications.length > 0 && (
            <Section title="Certifications" icon={<Award size={14} />}>
              <div className="space-y-3">
                {profile.certifications.map((c) => (
                  <div key={c.id}>
                    <div className="font-medium text-sm">{c.name}</div>
                    {c.issuer && <div className="text-sm text-muted-foreground">{c.issuer}</div>}
                    {c.issuedDate && (
                      <div className="text-xs text-muted-foreground mt-0.5">{c.issuedDate}</div>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {profile &&
            profile.experiences.length === 0 &&
            profile.education.length === 0 &&
            me.syncStatus !== 'done' && (
              <p className="text-xs text-muted-foreground text-center py-4">
                LinkedIn profile is still syncing…
              </p>
            )}
        </div>
      </PullToRefresh>
      {toast}
    </div>
  );
}

function OrgLogo({ url, fallback }: { url: string | null; fallback: React.ReactNode }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="w-11 h-11 rounded-md object-contain bg-muted shrink-0"
      />
    );
  }
  return (
    <div className="w-11 h-11 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0">
      {fallback}
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground pb-2">
        {icon}
        {title}
      </h2>
      <div className="rounded-xl border border-border bg-card p-4">{children}</div>
    </section>
  );
}
